import type { FC, ReactNode } from 'react';
import { Text, mergeClasses } from '@fluentui/react-components';
import { Calendar24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@/components/common/BasicButton';
import { SectionIcon } from '@/components/common/SectionIcon';
import { useBookAnotherCardStyles } from './BookAnotherCard.styles';

export interface BookAnotherCardProps {
  /** Click handler for the primary `New booking request` CTA. */
  onAction: () => void;
  /** Optional custom icon (defaults to a calendar glyph). */
  icon?: ReactNode;
  /** Optional class name merged onto the card root. */
  className?: string;
}

/**
 * Figma 2026 "Need to book another event?" footer card (node `1-1628`).
 * Soft-tinted full-width card with a section-icon chip on the left, a copy
 * block in the middle, and a primary navy CTA on the right.
 *
 * Pure presentation — i18n keys live under the `homeScreen.bookAnother.*`
 * namespace (en-US + fr-CA parity enforced by `locales.test.ts`).
 */
export const BookAnotherCard: FC<BookAnotherCardProps> = ({ onAction, icon, className }) => {
  const styles = useBookAnotherCardStyles();
  const { t } = useTranslation('homeScreen');

  return (
    <section
      className={mergeClasses(styles.root, className)}
      aria-label={t('homeScreen.bookAnother.title')}
    >
      <SectionIcon icon={icon ?? <Calendar24Regular />} className={styles.icon} />
      <div className={styles.copy}>
        <Text as="h3" className={styles.title}>
          {t('homeScreen.bookAnother.title')}
        </Text>
        <Text className={styles.description}>{t('homeScreen.bookAnother.description')}</Text>
      </div>
      <BasicButton
        variant="primary"
        figmaVariant
        size="medium"
        onClick={onAction}
        className={styles.bookButton}
      >
        <span className={styles.plusGlyph}>+</span>
        {t('homeScreen.bookAnother.action')}
      </BasicButton>
    </section>
  );
};

BookAnotherCard.displayName = 'BookAnotherCard';
